import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import api from '../services/api';

/* Impresión de reportes — A4, cabecera estilo cotización (logo + caja doc + caja empresa) */

const S = (n) => `S/ ${(parseFloat(n) || 0).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const N = (n) => {
  const v = parseFloat(n) || 0;
  return v % 1 === 0 ? v.toLocaleString('es-PE') : v.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const fmtFecha = (d) => {
  if (!d) return '—';
  const s = d instanceof Date ? d.toISOString().slice(0, 10) : String(d).slice(0, 10);
  const [y, m, dia] = s.split('-');
  return `${dia}/${m}/${y}`;
};

const API = process.env.REACT_APP_API_URL || 'http://localhost:5001';
const logoUrl = (empresa) => (empresa?.logo ? `${API}${empresa.logo}` : null);

const TITULOS = {
  ventas:            'Reporte de Ventas',
  compras:           'Reporte de Compras',
  utilidades:        'Reporte de Utilidades',
  productos:         'Reporte de Inventario',
  'cuentas-cobrar':  'Reporte de Cuentas por Cobrar',
  'cuentas-pagar':   'Reporte de Cuentas por Pagar',
};

const CSS_GLOBAL = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  @page { size: A4; margin: 0; }

  body.fmt-rep-a4 {
    font-family: Arial, Helvetica, sans-serif;
    font-size: 9pt;
    color: #111;
    background: #fff;
  }

  .repp-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
    margin-bottom: 8px;
  }
  .repp-logo { max-width: 150px; max-height: 85px; display: block; }
  .repp-logo-placeholder { width: 150px; }
  .repp-doc-box {
    border: 1.5px solid #222;
    border-radius: 4px;
    text-align: center;
    padding: 8px 14px;
    min-width: 210px;
  }
  .repp-doc-tipo { font-size: 11pt; font-weight: bold; text-transform: uppercase; line-height: 1.25; }
  .repp-doc-num  { font-size: 9pt; font-weight: bold; margin-top: 5px; }

  .repp-empresa-box {
    border: 1.5px solid #222;
    border-radius: 10px;
    text-align: center;
    padding: 8px 12px;
    margin-bottom: 8px;
  }
  .repp-empresa-nombre { font-size: 12pt; font-weight: bold; text-transform: uppercase; }
  .repp-empresa-linea  { font-size: 8.5pt; color: #333; margin-top: 2px; }

  .repp-info {
    border: 1px dashed #666;
    padding: 6px 10px;
    margin-bottom: 10px;
    font-size: 9pt;
    display: flex;
    gap: 28px;
    flex-wrap: wrap;
  }
  .repp-info-item { display: flex; gap: 6px; align-items: baseline; }
  .repp-info-key  { font-weight: bold; }

  /* KPIs resumen */
  .repp-kpis {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 10px;
  }
  .repp-kpi {
    flex: 1;
    min-width: 110px;
    border: 1px solid #222;
    border-radius: 4px;
    padding: 5px 8px;
    text-align: center;
  }
  .repp-kpi-label { font-size: 6.8pt; font-weight: bold; text-transform: uppercase; color: #444; letter-spacing: .3px; }
  .repp-kpi-valor { font-size: 10.5pt; font-weight: bold; margin-top: 2px; }

  .repp-seccion {
    font-size: 9.5pt;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: .5px;
    border-bottom: 1.5px solid #222;
    padding-bottom: 2px;
    margin: 12px 0 5px;
  }

  .repp-tabla { width: 100%; border-collapse: collapse; margin-bottom: 6px; font-size: 8.2pt; }
  .repp-tabla th {
    border: 1px solid #222;
    background: #efefef;
    padding: 3px 6px;
    font-size: 7.8pt;
    font-weight: bold;
    text-align: center;
  }
  .repp-tabla td {
    border-left: 1px solid #999;
    border-right: 1px solid #999;
    border-bottom: 1px dotted #bbb;
    padding: 2.5px 6px;
    vertical-align: top;
  }
  .repp-tabla tbody tr:last-child td { border-bottom: 1px solid #222; }
  .repp-tabla tfoot td {
    border: 1px solid #222;
    background: #f5f5f5;
    font-weight: bold;
    padding: 3px 6px;
  }
  .ctr { text-align: center; }
  .num { text-align: right; }
  .repp-vacio { text-align: center; font-style: italic; color: #666; padding: 6px !important; }

  .repp-footer {
    margin-top: 14px;
    border-top: 1.5px solid #222;
    padding-top: 5px;
    font-size: 7.5pt;
    color: #333;
    display: flex;
    justify-content: space-between;
  }

  @media screen {
    body { background: #d0d0d0 !important; }
    .repp-hoja {
      background: #fff;
      margin: 20px auto;
      padding: 20px;
      max-width: 210mm;
      box-shadow: 0 2px 12px rgba(0,0,0,.3);
    }
  }
  @media print {
    body { background: #fff !important; }
    .repp-hoja { box-shadow: none !important; margin: 0 !important; padding: 10mm 12mm !important; }
    .repp-seccion { page-break-after: avoid; }
    .repp-tabla tr { page-break-inside: avoid; }
  }
`;

function Kpi({ label, valor }) {
  return (
    <div className="repp-kpi">
      <div className="repp-kpi-label">{label}</div>
      <div className="repp-kpi-valor">{valor}</div>
    </div>
  );
}

function Tabla({ titulo, cols, rows, totales }) {
  return (
    <>
      <div className="repp-seccion">{titulo}</div>
      <table className="repp-tabla">
        <thead>
          <tr>{cols.map((c) => <th key={c.k}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows?.length ? rows.map((r, i) => (
            <tr key={i}>
              {cols.map((c) => (
                <td key={c.k} className={c.align || ''}>{c.fmt ? c.fmt(r[c.k], r) : (r[c.k] ?? '—')}</td>
              ))}
            </tr>
          )) : (
            <tr><td colSpan={cols.length} className="repp-vacio">Sin datos</td></tr>
          )}
        </tbody>
        {totales && rows?.length > 0 && (
          <tfoot>
            <tr>
              {cols.map((c, i) => (
                <td key={c.k} className={c.align || ''}>
                  {i === 0 ? 'TOTAL' : (totales[c.k] !== undefined ? totales[c.k] : '')}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </>
  );
}

const sum = (rows, k) => (rows || []).reduce((a, r) => a + (parseFloat(r[k]) || 0), 0);

export default function ReporteImprimirPage() {
  const { tipo } = useParams();
  const [sp] = useSearchParams();
  const [data, setData] = useState(null);
  const [empresa, setEmpresa] = useState(null);
  const [error, setError] = useState('');
  const [listo, setListo] = useState(false);

  const desde = sp.get('desde');
  const hasta = sp.get('hasta');

  useEffect(() => {
    document.body.className = 'fmt-rep-a4';
    return () => { document.body.className = ''; };
  }, []);

  useEffect(() => {
    const params = { desde, hasta };
    if (sp.get('almacen_id')) params.almacen_id = sp.get('almacen_id');
    if (sp.get('dias_sin_mov')) params.dias_sin_mov = sp.get('dias_sin_mov');
    if (sp.get('cliente_id')) params.cliente_id = sp.get('cliente_id');
    if (sp.get('proveedor_id')) params.proveedor_id = sp.get('proveedor_id');
    Promise.all([
      api.get(`/api/reportes/${tipo}`, { params }),
      api.get('/api/empresa'),
    ])
      .then(([rr, re]) => {
        document.title = `${TITULOS[tipo] || 'Reporte'} ${desde} al ${hasta}`;
        setData(rr.data?.data || null);
        setEmpresa(re.data?.empresa || re.data?.data || re.data);
        setListo(true);
      })
      .catch(() => setError('No se pudo cargar el reporte.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tipo]);

  useEffect(() => {
    if (!listo) return;
    const t = setTimeout(() => window.print(), 800);
    return () => clearTimeout(t);
  }, [listo]);

  if (error) return <><style>{CSS_GLOBAL}</style><div style={{ padding: 20, color: 'red' }}>{error}</div></>;
  if (!listo || !data) return <><style>{CSS_GLOBAL}</style><div style={{ padding: 20 }}>Generando reporte…</div></>;

  const rs = data.resumen || {};
  const margen = parseFloat(rs.ingresos) > 0 ? (parseFloat(rs.utilidad) / parseFloat(rs.ingresos)) * 100 : 0;
  const ahora = new Date();

  return (
    <>
      <style>{CSS_GLOBAL}</style>
      <div className="repp-hoja">

        {/* Encabezado: logo + caja documento */}
        <div className="repp-header">
          {logoUrl(empresa)
            ? <img src={logoUrl(empresa)} alt="logo" className="repp-logo" />
            : <div className="repp-logo-placeholder" />}
          <div className="repp-doc-box">
            <div className="repp-doc-tipo">{TITULOS[tipo] || 'Reporte'}</div>
            <div className="repp-doc-num">Del {fmtFecha(desde)} al {fmtFecha(hasta)}</div>
          </div>
        </div>

        {/* Caja empresa */}
        <div className="repp-empresa-box">
          <div className="repp-empresa-nombre">{empresa?.razon_social || 'EMPRESA'}</div>
          {empresa?.ruc && <div className="repp-empresa-linea">RUC: {empresa.ruc}</div>}
          {empresa?.direccion && <div className="repp-empresa-linea">{empresa.direccion}</div>}
          {empresa?.telefono && <div className="repp-empresa-linea">Cel.: {empresa.telefono}</div>}
        </div>

        {/* Franja info */}
        <div className="repp-info">
          <div className="repp-info-item"><span className="repp-info-key">Periodo:</span><span>{fmtFecha(desde)} — {fmtFecha(hasta)}</span></div>
          <div className="repp-info-item"><span className="repp-info-key">Emitido:</span><span>{fmtFecha(iso(ahora))} {ahora.toTimeString().slice(0, 5)}</span></div>
          <div className="repp-info-item"><span className="repp-info-key">Moneda:</span><span>Soles (S/)</span></div>
        </div>

        {/* ══ VENTAS ══ */}
        {tipo === 'ventas' && (
          <>
            <div className="repp-kpis">
              <Kpi label="N° Ventas" valor={N(rs.nro_ventas)} />
              <Kpi label="Total Vendido" valor={S(rs.total)} />
              <Kpi label="Contado" valor={S(rs.total_contado)} />
              <Kpi label="Crédito" valor={S(rs.total_credito)} />
              <Kpi label="Descuentos" valor={S(rs.descuentos)} />
              <Kpi label="Anuladas" valor={`${N(rs.anuladas_nro)} (${S(rs.anuladas_monto)})`} />
            </div>

            <Tabla
              titulo="Ventas por día"
              cols={[
                { k: 'fecha', label: 'Fecha', align: 'ctr', fmt: fmtFecha },
                { k: 'nro', label: 'N° Ventas', align: 'ctr', fmt: N },
                { k: 'total', label: 'Total', align: 'num', fmt: S },
              ]}
              rows={data.por_dia}
              totales={{ nro: N(sum(data.por_dia, 'nro')), total: S(sum(data.por_dia, 'total')) }}
            />
            <Tabla
              titulo="Por forma de pago"
              cols={[
                { k: 'forma_pago', label: 'Forma de pago' },
                { k: 'nro', label: 'N°', align: 'ctr', fmt: N },
                { k: 'total', label: 'Total', align: 'num', fmt: S },
              ]}
              rows={data.por_forma_pago}
            />
            <Tabla
              titulo="Top productos vendidos"
              cols={[
                { k: 'codigo', label: 'Código', align: 'ctr' },
                { k: 'nombre', label: 'Producto' },
                { k: 'cantidad', label: 'Cantidad', align: 'ctr', fmt: N },
                { k: 'total', label: 'Total', align: 'num', fmt: S },
              ]}
              rows={data.top_productos}
            />
            <Tabla
              titulo="Top clientes"
              cols={[
                { k: 'cliente', label: 'Cliente' },
                { k: 'nro', label: 'N° Compras', align: 'ctr', fmt: N },
                { k: 'total', label: 'Total', align: 'num', fmt: S },
              ]}
              rows={data.top_clientes}
            />
            <Tabla
              titulo="Por vendedor"
              cols={[
                { k: 'usuario', label: 'Vendedor' },
                { k: 'nro', label: 'N° Ventas', align: 'ctr', fmt: N },
                { k: 'total', label: 'Total', align: 'num', fmt: S },
              ]}
              rows={data.por_usuario}
            />
            <Tabla
              titulo="Detalle de ventas"
              cols={[
                { k: 'numero', label: 'Número', align: 'ctr' },
                { k: 'fecha', label: 'Fecha', align: 'ctr', fmt: fmtFecha },
                { k: 'cliente', label: 'Cliente' },
                { k: 'documento', label: 'Doc.', align: 'ctr' },
                { k: 'condicion_pago', label: 'Cond.', align: 'ctr' },
                { k: 'estado', label: 'Estado', align: 'ctr' },
                { k: 'total', label: 'Total', align: 'num', fmt: S },
              ]}
              rows={data.listado}
            />
          </>
        )}

        {/* ══ COMPRAS ══ */}
        {tipo === 'compras' && (
          <>
            <div className="repp-kpis">
              <Kpi label="N° Compras" valor={N(rs.nro_compras)} />
              <Kpi label="Total" valor={S(rs.total)} />
              <Kpi label="Promedio" valor={S(rs.compra_promedio)} />
              <Kpi label="Contado" valor={S(rs.total_contado)} />
              <Kpi label="Crédito" valor={S(rs.total_credito)} />
              <Kpi label="Anuladas" valor={`${N(rs.anuladas_nro)} (${S(rs.anuladas_monto)})`} />
            </div>

            <Tabla
              titulo="Compras por día"
              cols={[
                { k: 'fecha', label: 'Fecha', align: 'ctr', fmt: fmtFecha },
                { k: 'nro', label: 'N°', align: 'ctr', fmt: N },
                { k: 'total', label: 'Total', align: 'num', fmt: S },
              ]}
              rows={data.por_dia}
              totales={{ nro: N(sum(data.por_dia, 'nro')), total: S(sum(data.por_dia, 'total')) }}
            />
            <Tabla
              titulo="Por proveedor"
              cols={[
                { k: 'proveedor', label: 'Proveedor' },
                { k: 'nro', label: 'N°', align: 'ctr', fmt: N },
                { k: 'total', label: 'Total', align: 'num', fmt: S },
              ]}
              rows={data.por_proveedor}
            />
            <Tabla
              titulo="Top productos comprados"
              cols={[
                { k: 'codigo', label: 'Código', align: 'ctr' },
                { k: 'nombre', label: 'Producto' },
                { k: 'cantidad', label: 'Cantidad', align: 'ctr', fmt: N },
                { k: 'total', label: 'Total', align: 'num', fmt: S },
              ]}
              rows={data.top_productos}
            />
            <Tabla
              titulo="Detalle de compras"
              cols={[
                { k: 'numero', label: 'Número', align: 'ctr' },
                { k: 'fecha', label: 'Fecha', align: 'ctr', fmt: fmtFecha },
                { k: 'proveedor', label: 'Proveedor' },
                { k: 'tipo_documento', label: 'Doc.', align: 'ctr' },
                { k: 'condicion_pago', label: 'Cond.', align: 'ctr' },
                { k: 'estado', label: 'Estado', align: 'ctr' },
                { k: 'total', label: 'Total', align: 'num', fmt: S },
              ]}
              rows={data.listado}
            />
          </>
        )}

        {/* ══ UTILIDADES ══ */}
        {tipo === 'utilidades' && (
          <>
            <div className="repp-kpis">
              <Kpi label="Ingresos" valor={S(rs.ingresos)} />
              <Kpi label="Costo" valor={S(rs.costo)} />
              <Kpi label="Utilidad Bruta" valor={S(rs.utilidad)} />
              <Kpi label="Margen" valor={`${margen.toFixed(1)}%`} />
              <Kpi label="Descuentos" valor={S(rs.descuentos)} />
            </div>

            <Tabla
              titulo="Utilidad por día"
              cols={[
                { k: 'fecha', label: 'Fecha', align: 'ctr', fmt: fmtFecha },
                { k: 'ingresos', label: 'Ingresos', align: 'num', fmt: S },
                { k: 'costo', label: 'Costo', align: 'num', fmt: S },
                { k: 'utilidad', label: 'Utilidad', align: 'num', fmt: S },
              ]}
              rows={data.por_dia}
              totales={{
                ingresos: S(sum(data.por_dia, 'ingresos')),
                costo: S(sum(data.por_dia, 'costo')),
                utilidad: S(sum(data.por_dia, 'utilidad')),
              }}
            />
            <Tabla
              titulo="Utilidad por producto"
              cols={[
                { k: 'codigo', label: 'Código', align: 'ctr' },
                { k: 'nombre', label: 'Producto' },
                { k: 'cantidad', label: 'Cant.', align: 'ctr', fmt: N },
                { k: 'ingresos', label: 'Ingresos', align: 'num', fmt: S },
                { k: 'costo', label: 'Costo', align: 'num', fmt: S },
                { k: 'utilidad', label: 'Utilidad', align: 'num', fmt: S },
                { k: 'margen', label: 'Margen', align: 'num', fmt: (_, r) => parseFloat(r.ingresos) > 0 ? `${((parseFloat(r.utilidad) / parseFloat(r.ingresos)) * 100).toFixed(1)}%` : '—' },
              ]}
              rows={data.por_producto}
            />
            <Tabla
              titulo="Utilidad por familia"
              cols={[
                { k: 'familia', label: 'Familia' },
                { k: 'ingresos', label: 'Ingresos', align: 'num', fmt: S },
                { k: 'costo', label: 'Costo', align: 'num', fmt: S },
                { k: 'utilidad', label: 'Utilidad', align: 'num', fmt: S },
              ]}
              rows={data.por_familia}
              totales={{
                ingresos: S(sum(data.por_familia, 'ingresos')),
                costo: S(sum(data.por_familia, 'costo')),
                utilidad: S(sum(data.por_familia, 'utilidad')),
              }}
            />
          </>
        )}

        {/* ══ PRODUCTOS / INVENTARIO ══ */}
        {tipo === 'productos' && (
          <>
            <div className="repp-kpis">
              <Kpi label="Productos" valor={N(rs.nro_productos)} />
              <Kpi label="Unidades" valor={N(rs.unidades)} />
              <Kpi label="Valor a costo" valor={S(rs.valor_costo)} />
              <Kpi label="Valor a venta" valor={S(rs.valor_venta)} />
            </div>

            <Tabla
              titulo="Valorización por almacén"
              cols={[
                { k: 'almacen', label: 'Almacén' },
                { k: 'nro_productos', label: 'Productos', align: 'ctr', fmt: N },
                { k: 'unidades', label: 'Unidades', align: 'ctr', fmt: N },
                { k: 'valor_costo', label: 'Valor costo', align: 'num', fmt: S },
              ]}
              rows={data.valorizacion_por_almacen}
              totales={{ valor_costo: S(sum(data.valorizacion_por_almacen, 'valor_costo')) }}
            />
            <Tabla
              titulo="Stock bajo mínimo"
              cols={[
                { k: 'codigo', label: 'Código', align: 'ctr' },
                { k: 'nombre', label: 'Producto' },
                { k: 'stock_actual', label: 'Stock', align: 'ctr', fmt: N },
                { k: 'stock_minimo', label: 'Mínimo', align: 'ctr', fmt: N },
                { k: 'faltante', label: 'Faltante', align: 'ctr', fmt: N },
              ]}
              rows={data.stock_bajo}
            />
            <Tabla
              titulo="Productos sin movimiento"
              cols={[
                { k: 'codigo', label: 'Código', align: 'ctr' },
                { k: 'nombre', label: 'Producto' },
                { k: 'stock_actual', label: 'Stock', align: 'ctr', fmt: N },
                { k: 'valor_costo', label: 'Valor costo', align: 'num', fmt: S },
                { k: 'ultima_venta', label: 'Última venta', align: 'ctr', fmt: (v) => v ? fmtFecha(v) : 'Nunca' },
              ]}
              rows={data.sin_movimiento}
            />
            <Tabla
              titulo="Inventario valorizado"
              cols={[
                { k: 'codigo', label: 'Código', align: 'ctr' },
                { k: 'nombre', label: 'Producto' },
                { k: 'familia', label: 'Familia' },
                { k: 'stock', label: 'Stock', align: 'ctr', fmt: N },
                { k: 'costo_unit', label: 'Costo unit.', align: 'num', fmt: S },
                { k: 'valor_costo', label: 'Valor total', align: 'num', fmt: S },
              ]}
              rows={data.listado}
              totales={{ valor_costo: S(sum(data.listado, 'valor_costo')) }}
            />
          </>
        )}

        {/* ══ CUENTAS POR COBRAR ══ */}
        {tipo === 'cuentas-cobrar' && (
          <>
            <div className="repp-kpis">
              <Kpi label="N° Cuentas" valor={N(rs.nro_cuentas)} />
              <Kpi label="Total Crédito" valor={S(rs.total)} />
              <Kpi label="Cobrado" valor={S(rs.cobrado)} />
              <Kpi label="Saldo Pendiente" valor={S(rs.saldo)} />
              <Kpi label="Vencido" valor={`${S(rs.saldo_vencido)} (${N(rs.nro_vencidas)})`} />
              <Kpi label="Cobros del periodo" valor={`${S(rs.cobros_total)} (${N(rs.cobros_nro)})`} />
            </div>

            <Tabla
              titulo="Antigüedad de la deuda pendiente (días transcurridos)"
              cols={[
                { k: 'rango', label: 'Días desde que se generó la deuda' },
                { k: 'nro', label: 'N° Cuentas', align: 'ctr', fmt: N },
                { k: 'saldo', label: 'Saldo', align: 'num', fmt: S },
              ]}
              rows={data.antiguedad}
              totales={{ nro: N(sum(data.antiguedad, 'nro')), saldo: S(sum(data.antiguedad, 'saldo')) }}
            />
            <Tabla
              titulo="Saldos por cliente"
              cols={[
                { k: 'cliente', label: 'Cliente' },
                { k: 'documento', label: 'Documento', align: 'ctr' },
                { k: 'nro', label: 'N°', align: 'ctr', fmt: N },
                { k: 'total', label: 'Crédito', align: 'num', fmt: S },
                { k: 'cobrado', label: 'Cobrado', align: 'num', fmt: S },
                { k: 'saldo', label: 'Saldo', align: 'num', fmt: S },
              ]}
              rows={data.por_cliente}
              totales={{
                total: S(sum(data.por_cliente, 'total')),
                cobrado: S(sum(data.por_cliente, 'cobrado')),
                saldo: S(sum(data.por_cliente, 'saldo')),
              }}
            />
            <Tabla
              titulo="Detalle de cuentas"
              cols={[
                { k: 'venta_numero', label: 'Venta', align: 'ctr' },
                { k: 'fecha', label: 'Fecha', align: 'ctr', fmt: fmtFecha },
                { k: 'cliente', label: 'Cliente' },
                { k: 'monto', label: 'Crédito', align: 'num', fmt: S },
                { k: 'monto_cobrado', label: 'Cobrado', align: 'num', fmt: S },
                { k: 'saldo', label: 'Saldo', align: 'num', fmt: S },
                { k: 'fecha_vencimiento', label: 'Vence', align: 'ctr', fmt: (v) => v ? fmtFecha(v) : '—' },
                { k: 'dias_vencido', label: 'Días venc.', align: 'ctr', fmt: (v) => parseFloat(v) > 0 ? N(v) : '—' },
                { k: 'estado', label: 'Estado', align: 'ctr' },
              ]}
              rows={data.listado}
            />
            <Tabla
              titulo="Cobros del periodo"
              cols={[
                { k: 'fecha', label: 'Fecha', align: 'ctr', fmt: fmtFecha },
                { k: 'venta_numero', label: 'Venta', align: 'ctr' },
                { k: 'cliente', label: 'Cliente' },
                { k: 'forma_pago', label: 'Forma de pago', align: 'ctr' },
                { k: 'referencia', label: 'Referencia' },
                { k: 'usuario', label: 'Usuario' },
                { k: 'monto', label: 'Monto', align: 'num', fmt: S },
              ]}
              rows={data.cobros}
              totales={{ monto: S(sum(data.cobros, 'monto')) }}
            />
          </>
        )}

        {/* ══ CUENTAS POR PAGAR ══ */}
        {tipo === 'cuentas-pagar' && (
          <>
            <div className="repp-kpis">
              <Kpi label="N° Cuentas" valor={N(rs.nro_cuentas)} />
              <Kpi label="Total Crédito" valor={S(rs.total)} />
              <Kpi label="Pagado" valor={S(rs.pagado)} />
              <Kpi label="Saldo por Pagar" valor={S(rs.saldo)} />
              <Kpi label="Pagos del periodo" valor={`${S(rs.pagos_total)} (${N(rs.pagos_nro)})`} />
            </div>

            <Tabla
              titulo="Saldos por proveedor"
              cols={[
                { k: 'proveedor', label: 'Proveedor' },
                { k: 'ruc', label: 'RUC', align: 'ctr' },
                { k: 'nro', label: 'N°', align: 'ctr', fmt: N },
                { k: 'total', label: 'Crédito', align: 'num', fmt: S },
                { k: 'pagado', label: 'Pagado', align: 'num', fmt: S },
                { k: 'saldo', label: 'Saldo', align: 'num', fmt: S },
              ]}
              rows={data.por_proveedor}
              totales={{
                total: S(sum(data.por_proveedor, 'total')),
                pagado: S(sum(data.por_proveedor, 'pagado')),
                saldo: S(sum(data.por_proveedor, 'saldo')),
              }}
            />
            <Tabla
              titulo="Detalle de cuentas"
              cols={[
                { k: 'compra_numero', label: 'Compra', align: 'ctr' },
                { k: 'fecha', label: 'Fecha', align: 'ctr', fmt: fmtFecha },
                { k: 'proveedor', label: 'Proveedor' },
                { k: 'monto', label: 'Crédito', align: 'num', fmt: S },
                { k: 'monto_pagado', label: 'Pagado', align: 'num', fmt: S },
                { k: 'saldo', label: 'Saldo', align: 'num', fmt: S },
                { k: 'estado', label: 'Estado', align: 'ctr' },
              ]}
              rows={data.listado}
            />
            <Tabla
              titulo="Pagos del periodo"
              cols={[
                { k: 'fecha', label: 'Fecha', align: 'ctr', fmt: fmtFecha },
                { k: 'compra_numero', label: 'Compra', align: 'ctr' },
                { k: 'proveedor', label: 'Proveedor' },
                { k: 'forma_pago', label: 'Forma de pago', align: 'ctr' },
                { k: 'referencia', label: 'Referencia' },
                { k: 'usuario', label: 'Usuario' },
                { k: 'monto', label: 'Monto', align: 'num', fmt: S },
              ]}
              rows={data.pagos}
              totales={{ monto: S(sum(data.pagos, 'monto')) }}
            />
          </>
        )}

        <div className="repp-footer">
          <span>{empresa?.razon_social || ''}</span>
          <span>Generado por el sistema — {fmtFecha(iso(ahora))}</span>
        </div>
      </div>
    </>
  );
}

function iso(d) { return d.toISOString().slice(0, 10); }
